import { useEffect } from 'react';
import { useDeckStore } from '../store/deckStore';

/** True while the user is typing somewhere the browser should handle keys itself. */
function isEditingText(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Editor keyboard shortcuts:
 * ⌘/Ctrl+Z undo · ⌘/Ctrl+Shift+Z or Ctrl+Y redo · ⌘/Ctrl+D duplicate element (or slide) ·
 * Delete/Backspace remove selection · arrows nudge · ↑/↓ change slide when nothing selected · Esc deselect.
 * Shift/⌘-click multi-select is handled on the canvas.
 */
export function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useDeckStore.getState();
      if (s.presenting) return;
      if (e.key === 'Escape' && e.target instanceof HTMLElement && e.target.isContentEditable) {
        e.target.blur();
        return;
      }
      if (isEditingText(e.target)) return;
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden)')) return;

      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      const slide = s.slides[s.activeSlideIndex];
      const selectedIds = s.selectedElementIds.filter((id) => {
        const el = slide?.elements.find((e) => e.id === id);
        return el && !el.locked;
      });
      const primary = selectedIds.length
        ? slide?.elements.find((el) => el.id === selectedIds[selectedIds.length - 1])
        : null;

      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }
      if (mod && key === 'y') {
        e.preventDefault();
        s.redo();
        return;
      }
      if (mod && key === 'd') {
        e.preventDefault();
        if (primary) s.duplicateElement(primary.id);
        else s.duplicateSlide();
        return;
      }
      if (mod) return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length) {
        e.preventDefault();
        s.removeSelectedElements();
        return;
      }
      if (e.key === 'Escape') {
        s.selectElement(null);
        return;
      }

      const arrows: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const dir = arrows[e.key];
      if (!dir) return;
      e.preventDefault();
      if (selectedIds.length) {
        const step = e.shiftKey ? 10 : 1;
        s.pushHistory(`nudge:${selectedIds.join(',')}`);
        s.nudgeElements(selectedIds, dir[0] * step, dir[1] * step);
      } else if (dir[1] !== 0) {
        const next = Math.min(Math.max(s.activeSlideIndex + dir[1], 0), s.slides.length - 1);
        s.setActiveSlide(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
